import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import {
  createCreativeApi,
  creativeFileName,
  downloadCreative,
  getCreativesStatusApi,
  suggestContentApi,
  type CreativesStatus,
  type GeneratedCreative,
} from "../criativosApi";
import { CRIATIVO_PAGE, CRIATIVOS_PAGE } from "../routes";
import { CreativeImage, GeneratingNotice, ReviewBox } from "./CreativeParts";
import UseCreativeAsAdModal from "./UseCreativeAsAdModal";

type Props = {
  campaignId: string;
  /** Ainda cabe anúncio (até 5) e a campanha não está encerrada. */
  canAddAd: boolean;
  /** Depois de incluir o anúncio: recarrega a campanha. */
  onAdAdded: () => void;
};

/**
 * Criativo da campanha direto na página dela: a Eco-IA escreve os textos a
 * partir da campanha, o Google desenha no padrão da empresa e a Eco-IA confere.
 * A arte aparece aqui, pronta para virar anúncio.
 */
const CampaignCreativePanel: React.FC<Props> = ({ campaignId, canAddAd, onAdAdded }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CreativesStatus | null>(null);
  const [step, setStep] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedCreative | null>(null);
  const [useAsAd, setUseAsAd] = useState(false);

  useEffect(() => {
    getCreativesStatusApi()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const blocked = !status
    ? null
    : !status.imageEnabled
      ? "O gerador de imagens não está configurado no servidor."
      : !status.reference
        ? "Falta cadastrar o criativo padrão da empresa (Campanhas → Criativos, por um administrador)."
        : null;

  const generate = async () => {
    try {
      setResult(null);
      setStep("A Eco-IA está escrevendo os textos da arte");
      const content = await suggestContentApi({ campaignId });
      setStep("Gerando o criativo desta campanha");
      const out = await createCreativeApi({ campaignId, content: content.content, photoPrompt: content.photoPrompt, format: "FEED" });
      setResult(out);
      toast.success(out.review?.approved ? "Criativo pronto e conferido pela Eco-IA." : "Criativo pronto. Veja o que a Eco-IA apontou.");
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setStep(null);
    }
  };

  return (
    <section className="crvCampaign" aria-label="Criativo desta campanha">
      <div className="crvCampaign__head">
        <div>
          <h3 className="cmpPanel__title">
            <EcoIcon name="camera" size={18} /> Criativo desta campanha
          </h3>
          <p className="cmpHint">
            A Eco-IA escreve os textos a partir desta campanha e gera a arte no padrão da empresa, em 1 a 3 minutos. Depois é só usar no anúncio.
          </p>
        </div>
        <div className="cmpActions">
          <button type="button" className="ecoBtn" onClick={() => navigate(`${CRIATIVOS_PAGE()}?campanha=${campaignId}`)}>
            Ver os criativos desta campanha
          </button>
          <button type="button" className="ecoBtn ecoBtn--primary" onClick={generate} disabled={Boolean(step) || Boolean(blocked) || !status}>
            <EcoIcon name="camera" size={18} />
            {result ? "Gerar outro" : "Gerar criativo desta campanha"}
          </button>
        </div>
      </div>

      {blocked ? (
        <p className="cmpNotice">
          <EcoIcon name="alertTriangle" size={16} />
          <span>{blocked}</span>
        </p>
      ) : null}

      {step ? <GeneratingNotice label={step} /> : null}

      {result && !step ? (
        <div className="crvCampaign__result">
          <CreativeImage id={result.id} className="crvCampaign__img" alt={`Criativo ${result.title}`} />
          <div className="crvCampaign__side">
            <ReviewBox review={result.review} />
            <div className="cmpActions crvCampaign__actions">
              {canAddAd ? (
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setUseAsAd(true)}>
                  <EcoIcon name="megaphone" size={16} />
                  Usar no anúncio
                </button>
              ) : null}
              <button type="button" className="ecoBtn" onClick={() => navigate(CRIATIVO_PAGE(result.id))}>
                Ajustar no estúdio
              </button>
              <button
                type="button"
                className="ecoBtn"
                onClick={() => downloadCreative(result.id, creativeFileName(result.title, result.version)).catch((e) => toast.error(e?.message))}
              >
                <EcoIcon name="download" size={16} />
                Baixar
              </button>
            </div>
            <p className="cmpHint">No estúdio dá para trocar os textos, pedir mudanças na imagem e gerar a versão para Stories.</p>
          </div>
        </div>
      ) : null}

      {useAsAd && result ? (
        <UseCreativeAsAdModal
          creativeId={result.id}
          defaultCampaignId={campaignId}
          onClose={() => setUseAsAd(false)}
          onDone={() => {
            setUseAsAd(false);
            onAdAdded();
          }}
        />
      ) : null}
    </section>
  );
};

export default CampaignCreativePanel;
